import type { JSX } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import type { PortfolioPerformance } from "@finance-os/shared-types/api";

import { formatDay, usePortfolioFormat } from "../../lib/use-portfolio-format.js";
import {
  chartSeries,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../ui/chart.js";
import { PanelCard, type PanelQuery } from "./PanelCard.js";

// Two scales: NAV is orders of magnitude above P&L, so P&L gets its own axis.
const config = {
  nav_eur_minor: { label: "NAV", color: chartSeries(0).color },
  cumulative_pl_eur_minor: { label: "P&L (net of deposits)", color: chartSeries(1).color },
} satisfies ChartConfig;

/** Message for a history too short to chart, or null when there is enough. */
function shortHistoryNote(data: PortfolioPerformance): string | null {
  const [first] = data.points;
  if (!first) return "No snapshots yet — performance appears after the first broker sync.";
  if (data.points.length < 2) {
    return `Only one day of history so far (${formatDay(first.date)}) — the chart needs at least two.`;
  }
  return null;
}

/** NAV and cumulative P&L over the selected window, one point per day. */
export function PerformancePanel({ query }: { query: PanelQuery<PortfolioPerformance> }): JSX.Element {
  const fmt = usePortfolioFormat();
  const data = query.data;
  // The window is clamped to the history start, so they meet when history is shorter than the period.
  const startsAtHistory = data?.history_start != null && data.history_start >= data.window.from;

  return (
    <PanelCard
      title="Performance"
      isLoading={query.isLoading}
      isError={query.isError}
      empty={data ? shortHistoryNote(data) : null}
    >
      {data && (
        <>
          <ChartContainer config={config} className="aspect-auto h-64 w-full">
            <LineChart data={data.points} margin={{ top: 4, left: 4, right: 4 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(day: string) => formatDay(day)}
              />
              <YAxis
                yAxisId="nav"
                width={56}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => fmt.compact(v)}
              />
              <YAxis
                yAxisId="pl"
                orientation="right"
                width={56}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => fmt.compact(v)}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(label: unknown) => (typeof label === "string" ? formatDay(label) : null)}
                    valueFormatter={(v) => fmt.money(v)}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              <Line
                yAxisId="nav"
                dataKey="nav_eur_minor"
                type="monotone"
                stroke="var(--color-nav_eur_minor)"
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="pl"
                dataKey="cumulative_pl_eur_minor"
                type="monotone"
                stroke="var(--color-cumulative_pl_eur_minor)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
          {startsAtHistory && data.history_start && (
            <p className="text-muted-foreground text-xs">
              History starts {formatDay(data.history_start)}, the first day both positions and cash were ingested.
            </p>
          )}
        </>
      )}
    </PanelCard>
  );
}
